import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface LinkBuildingPartner {
  id: string;
  brand_id: string;
  user_id: string;
  contact_name: string;
  email?: string;
  organization?: string;
  sector?: string;
  contact_info?: string;
  available_domains?: string[];
  notes?: string;
  preferred_communication_channel?: 'slack' | 'email' | 'whatsapp' | 'linkedin';
  contact_id?: string;
  created_at: string;
  updated_at: string;
}

// Helper to sync partner to os_contacts
const syncPartnerToContacts = async (
  brandId: string,
  partner: {
    id: string;
    contact_name: string;
    email?: string | null;
    organization?: string | null;
  }
): Promise<string | null> => {
  if (!partner.email) return null;

  // Parse contact_name into first/last name
  const nameParts = partner.contact_name.trim().split(/\s+/);
  const firstName = nameParts[0] || null;
  const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : null;

  try {
    const { data: contactId, error } = await supabase.rpc('upsert_os_contact', {
      p_brand_id: brandId,
      p_email: partner.email,
      p_first_name: firstName,
      p_last_name: lastName,
      p_phone: null,
      p_module: ['lb'],
      p_activity: ['exchange partner'],
      p_organization: partner.organization || null,
      p_title: null,
      p_user_id: null,
    });

    if (error) {
      console.error('Error syncing partner to contacts:', error);
      return null;
    }
    return contactId;
  } catch (err) {
    console.error('Exception syncing partner to contacts:', err);
    return null;
  }
};

export const useLinkBuildingPartners = (brandId: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: partners = [], isLoading } = useQuery({
    queryKey: ['link-building-partners', brandId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('os_lb_partners')
        .select('*')
        .eq('brand_id', brandId)
        .order('organization', { ascending: true });

      if (error) throw error;
      return data as LinkBuildingPartner[];
    },
    enabled: !!brandId,
  });

  const createPartner = useMutation({
    mutationFn: async (partner: Omit<LinkBuildingPartner, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('os_lb_partners')
        .insert([partner])
        .select()
        .single();

      if (error) throw error;

      // Sync to contacts
      if (data && data.email) {
        const contactId = await syncPartnerToContacts(brandId, data);
        if (contactId) {
          await supabase
            .from('os_lb_partners')
            .update({ contact_id: contactId })
            .eq('id', data.id);
        }
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['link-building-partners', brandId] });
      toast({ title: 'Partner created successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Error creating partner', description: error.message, variant: 'destructive' });
    },
  });

  const updatePartner = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<LinkBuildingPartner> & { id: string }) => {
      const { data, error } = await supabase
        .from('os_lb_partners')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Sync to contacts
      if (data && data.email) {
        const contactId = await syncPartnerToContacts(brandId, data);
        if (contactId) {
          await supabase
            .from('os_lb_partners')
            .update({ contact_id: contactId })
            .eq('id', data.id);
        }
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['link-building-partners', brandId] });
      toast({ title: 'Partner updated successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Error updating partner', description: error.message, variant: 'destructive' });
    },
  });

  const deletePartner = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('os_lb_partners')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['link-building-partners', brandId] });
      toast({ title: 'Partner deleted successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Error deleting partner', description: error.message, variant: 'destructive' });
    },
  });

  const syncAllToContacts = async (): Promise<number> => {
    let synced = 0;
    for (const partner of partners) {
      if (!partner.email) continue;
      const contactId = await syncPartnerToContacts(brandId, partner);
      if (contactId) {
        await supabase
          .from('os_lb_partners')
          .update({ contact_id: contactId })
          .eq('id', partner.id);
        synced++;
      }
    }
    return synced;
  };

  return {
    partners,
    isLoading,
    createPartner,
    updatePartner,
    deletePartner,
    syncAllToContacts,
  };
};
